import {gql} from '@apollo/client'
import {isRight} from 'fp-ts/lib/Either'
import reporter from 'io-ts-reporters'
import React, {useEffect, useMemo, useState} from 'react'
import {MAX_ROW_ERRORS_SHOWN} from '../../common/csv-import/af-bulk-send'
import {ProcessingAfBulkSendCsvUploadTaskMetadataCodec} from '../../common/types/csv-upload-task'
import {IconButton} from '../../components/library/buttons'
import {Copyable} from '../../components/library/copy'
import {NoResults} from '../../components/library/list'
import styled from 'styled-components'
import {Loader} from '../../components/loader'
import {EnvironmentNode} from '../../environment'
import {displayDate} from '../../lib/helpers'
import {ErrorInfo} from './error'
import {AfBulkSendActionFlowFragment} from './generated'
import {
  CsvUploadTaskForListAfBulkSendFragment,
  useCsvUploadTaskListAfBulkSendQuery
} from './generated/results'

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    padding: 0;
    margin: 0 0 5px;
  }
`

interface ResultContainerProps {
  environment: EnvironmentNode
  actionFlows: AfBulkSendActionFlowFragment[] | undefined
  title: string
}

export const ResultContainer = ({
  environment,
  actionFlows,
  title
}: ResultContainerProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | undefined>(
    undefined
  )
  /** Main task state */
  const {
    data: allTasksQueryResult,
    loading,
    called,
    // refetch: refetchAllTasks,
    startPolling
  } = useCsvUploadTaskListAfBulkSendQuery({
    variables: {
      environmentId: environment.id,
      organisationId: environment.organisationId
    }
  })
  const allTasks = useMemo(
    () => allTasksQueryResult?.csvUploadTasks?.nodes ?? [],
    [allTasksQueryResult?.csvUploadTasks?.nodes]
  )

  useEffect(() => {
    startPolling(2000)
  }, [startPolling])

  if (!called || loading) {
    return <Loader />
  }

  return (
    <>
      {allTasks.length === 0 && (
        <NoResults
          icon="regex"
          heading="No bulk sends yet"
          message="Bulk send status will appear here"
        />
      )}

      {allTasks.map((job, i) => (
        <JobSummary
          key={job.id}
          environment={environment}
          job={job}
          actionFlows={actionFlows}
          title={title}
          index={i}
          setExpandedIndex={setExpandedIndex}
          expandedIndex={expandedIndex}
        />
      ))}
    </>
  )
}

type JobSummaryProps = {
  environment: EnvironmentNode
  job: CsvUploadTaskForListAfBulkSendFragment
  actionFlows: AfBulkSendActionFlowFragment[] | undefined
  title: string
  index: number
  expandedIndex: number | undefined
  setExpandedIndex: React.Dispatch<React.SetStateAction<number | undefined>>
}
const JobSummary = ({
  job,
  actionFlows,
  title,
  index,
  setExpandedIndex,
  expandedIndex
}: JobSummaryProps) => {
  const invalidResponse = (
    <JobWrap style={{marginBottom: '20px'}}>
      <header>
        <h6>{displayDate(job.created)}</h6>
        <h4>Invalid data for job</h4>
      </header>
    </JobWrap>
  )
  const metadata = ProcessingAfBulkSendCsvUploadTaskMetadataCodec.decode(
    job.metadata
  )
  if (!isRight(metadata)) {
    console.error('job metadata is not valid', job, reporter.report(metadata))
    return invalidResponse
  }

  const targetActionFlow = actionFlows?.find(
    af => af.id === metadata.right.targetActionFlowId
  )
  const header =
    job.status === 'COMPLETED'
      ? 'Send complete'
      : job.status === 'PENDING'
      ? 'Job queued...'
      : job.status === 'FAILED'
      ? 'Send failed'
      : 'Send in progress...'

  const failed = job.status === 'FAILED'

  return (
    <JobWrap style={{marginBottom: '20px'}}>
      <header
        onClick={() =>
          setExpandedIndex(expandedIndex === index ? undefined : index)
        }
      >
        <h6>{displayDate(job.created)}</h6>
        {!failed && (
          <h4>
            {header}
            {metadata.right.rowErrors?.length ? (
              <span style={{opacity: 0.5, fontWeight: 'lighter'}}>
                {' '}
                with row errors
              </span>
            ) : (
              ''
            )}
          </h4>
        )}
        {failed && <ErrorInfo>{header}</ErrorInfo>}
      </header>
      <ToggleWrap>
        <Toggle
          expanded={expandedIndex === index}
          setExpanded={expanded =>
            setExpandedIndex(expanded ? index : undefined)
          }
        />
      </ToggleWrap>
      {expandedIndex === index && (
        <StyledList>
          {metadata.right.fileErrors?.length &&
            metadata.right.fileErrors.map(e => (
              <li key={e}>
                <ErrorDescription>{e}</ErrorDescription>
              </li>
            ))}
          <li>
            Target {title}: {targetActionFlow?.name}{' '}
            <span style={{opacity: 0.5}}>ID: {targetActionFlow?.id}</span>
          </li>
          <li>
            CSV Rows processed: {job.processedRowCount.toLocaleString()} /{' '}
            {job.rowCount.toLocaleString()}
          </li>
          <li>Test mode: {String(job.isTest)}</li>
          {job.processFrom && job.processTo && (
            <li>Duration: {getDuration(job.processFrom, job.processTo)}</li>
          )}
          <li>
            Users target properties:{' '}
            <ul>
              {metadata.right.userTargetProperties?.map((p, i) => (
                <li key={p} style={{paddingTop: i === 0 ? '5px' : ''}}>
                  {p}
                </li>
              ))}
            </ul>
          </li>
          {metadata.right.rowErrors?.length && (
            <>
              <hr />
              <p>
                Row errors:{' '}
                <span style={{opacity: 0.5, fontWeight: 'lighter'}}>
                  {' '}
                  limited to first {MAX_ROW_ERRORS_SHOWN} errors
                </span>
              </p>
              <ErrorDescription>
                <ul>
                  {metadata.right.rowErrors
                    .slice(0, MAX_ROW_ERRORS_SHOWN)
                    .map(e => {
                      return (
                        <li key={`line-${e.line}`}>
                          Line {e.line}: {e.errors?.join(', ')}
                        </li>
                      )
                    })}
                </ul>
              </ErrorDescription>
              <div style={{marginTop: '10px', alignSelf: 'flex-end'}}>
                <Copyable
                  value={metadata.right.rowErrors
                    .slice(0, MAX_ROW_ERRORS_SHOWN) // print the first few errors, when we get 1000s then it is impractical
                    .map(r => r.row)
                    .join('\n')}
                  copyText="Copy failed rows"
                  copiedText="Copied"
                />
              </div>
            </>
          )}
        </StyledList>
      )}
    </JobWrap>
  )
}

const ToggleWrap = styled.div``

const JobWrap = styled.div`
  border: 1px solid ${p => p.theme.grey10};
  border-radius: ${p => p.theme.borderRadius};
  padding: 20px;
  margin: 0 0 10px;
  position: relative;

  > header {
    margin: 0 0 15px;
    cursor: pointer;
    > h6 {
      color: ${p => p.theme.grey50};
      font-weight: normal;
      font-size: inherit;
      margin: 0 0 10px;
    }
    > h4 {
      margin: 0;
    }
  }

  ${ToggleWrap} {
    position: absolute;
    top: 0;
    right: 0;
    margin: 10px;
  }
`

const ErrorDescription = styled.span`
  color: ${p => p.theme.red};
`

const Toggle = ({
  expanded,
  setExpanded
}: {
  expanded: boolean
  setExpanded: (v: boolean) => void
}) => (
  <IconButton
    size="large"
    icon="chevron"
    variant="muted"
    rotateIcon={expanded ? -90 : 90}
    onClick={e => {
      e.preventDefault()
      setExpanded(!expanded)
    }}
  />
)

const getDuration = (from: string, to: string) => {
  const ms = new Date(to).getTime() - new Date(from).getTime()
  const seconds = ms / 1000
  const minutes = seconds / 60

  if (Math.round(minutes) >= 1) {
    return `${Math.round(minutes)}m`
  }

  return `${Math.round(Math.max(seconds, 1))}s`
}

gql`
  fragment CsvUploadTaskForListAfBulkSend on CsvUploadTask {
    id
    filename
    status
    created
    processedRowCount
    rowCount
    metadata
    processFrom
    processTo
    isTest
  }
`

gql`
  query csvUploadTaskListAFBulkSend(
    $organisationId: String!
    $environmentId: String!
    $cursor: Cursor
  ) {
    csvUploadTasks(
      first: 20
      after: $cursor
      orderBy: [CREATED_DESC]
      condition: {
        organisationId: $organisationId
        environmentId: $environmentId
        taskType: AF_BULK_SEND
      }
    ) {
      nodes {
        ...CsvUploadTaskForListAfBulkSend
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
